/* eslint no-restricted-syntax: 0, prefer-template: 0, guard-for-in: 0
   ---
   These rules are preventing the performance optimizations below.
 */

/**
 * Compose classes from multiple sources.
 *
 * @example
 * ```tsx
 * const slots = {
 *  root: ['root', 'primary'],
 *  label: ['label'],
 * };
 *
 * const getUtilityClass = (slot) => `MuiButton-${slot}`;
 *
 * const classes = {
 *   root: 'my-root-class',
 * };
 *
 * const output = composeClasses(slots, getUtilityClass, classes);
 * // {
 * //   root: 'MuiButton-root MuiButton-primary my-root-class',
 * //   label: 'MuiButton-label',
 * // }
 * ```
 *
 * @param slots a list of classes for each possible slot
 * @param getUtilityClass a function to resolve the class based on the slot name
 * @param classes the input classes from props
 * @param cssModules the CSS module classes to apply
 * @returns the resolved classes for all slots
 */
export default function composeClasses<ClassKey extends string>(
  slots: Record<ClassKey, ReadonlyArray<string | false | undefined | null>>,
  getUtilityClass: (slot: string) => string,
  classes: Record<string, string> | undefined = undefined,
  cssModules: Record<string, string> | undefined = undefined,
): Record<ClassKey, string> {
  const output: Record<ClassKey, string> = {} as any;

  for (const slotName in slots) {
    const slot = slots[slotName];
    let buffer = '';
    let start = true;

    for (let i = 0; i < slot.length; i += 1) {
      const value = slot[i];
      if (value) {
        const utilityClass = getUtilityClass(value);
        buffer += (start === true ? '' : ' ') + utilityClass;
        start = false;

        if (classes && classes[value]) {
          buffer += ' ' + classes[value];
        }

        if (cssModules && cssModules[utilityClass]) {
          buffer += ' ' + cssModules[utilityClass];
        }
      }
    }

    output[slotName] = buffer;
  }

  return output;
}
